import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
} from "firebase/firestore";
import { auth, db } from "../firebase";

// session.status: "loading" | "signedOut" | "noAccess" | "ready" | "error"
// When ready, session.role is "admin" or "distributor".
const AuthContext = createContext({ status: "loading" });

export const useSession = () => useContext(AuthContext);

export const signOutUser = () => signOut(auth);

// Owner accounts are listed only in firestore.rules (and the Cloud
// Function), never in this app, so nobody can read them from the page
// source. To recognise one, try a query that the rules allow only for admins.
async function rulesGrantAdmin() {
  try {
    await getDocs(query(collection(db, "users"), limit(1)));
    return true;
  } catch (error) {
    if (error.code === "permission-denied") return false;
    throw error;
  }
}

async function resolveSession(user) {
  const email = (user.email || "").toLowerCase();
  if (!email || !user.emailVerified) {
    return { status: "noAccess", user, email, reason: "unverified" };
  }
  const snapshot = await getDoc(doc(db, "users", email));
  const profile = snapshot.exists() ? snapshot.data() : null;
  if (profile?.active === true) {
    if (profile.role === "admin") {
      return { status: "ready", user, email, role: "admin", profile };
    }
    if (profile.role === "distributor" && profile.distributorId) {
      return {
        status: "ready",
        user,
        email,
        role: "distributor",
        distributorId: profile.distributorId,
        profile,
      };
    }
  }
  if (await rulesGrantAdmin()) {
    return { status: "ready", user, email, role: "admin", owner: true };
  }
  return { status: "noAccess", user, email, reason: "notRegistered" };
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState({ status: "loading" });

  useEffect(() => {
    let latest = 0;
    return onAuthStateChanged(auth, async (user) => {
      const run = ++latest;
      if (!user) {
        setSession({ status: "signedOut" });
        return;
      }
      setSession({ status: "loading" });
      try {
        const next = await resolveSession(user);
        if (run === latest) setSession(next);
      } catch (error) {
        console.error("Could not load portal access", error);
        if (run === latest) setSession({ status: "error", user, error });
      }
    });
  }, []);

  return (
    <AuthContext.Provider value={session}>{children}</AuthContext.Provider>
  );
}
